import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import useAddressApi from '../../commons/hooks/use-address-api';
import { selectMethods, selectShipping } from '../../modules/shipping/slice';
import useCartTotals from '../../store/cartTotals/useCartTotals';
import { currentBaseUrl } from '../../helpers/urlConst';
import { amountFormatter } from '../../helpers/format';
import DataContext from '../../store/dataContext';
import UiContext from '../../store/uiContext';
import useCurrency from '../../modules/currency/hooks/use-currency';

import { removeCoupon, refreshCertificatesAndCoupons } from './CouponServices';

import './CouponTotalContainer.scss';

const CouponTotalContainer = ({ gift, type }) => {
  const { t } = useTranslation();
  const isCoupon = type === 'coupon';
  const {
    cartId,
    device,
    email,
    contextShippingAddress,
    updateCoupon,
    coupon,
    giftCertificates,
    updateShippingMethods,
    updateShippingMethodId,
    shippingMethodId,
    updateGiftInfo,
    setPaymentMethodData,
    additionalCharges,
  } = useContext(DataContext);
  const { isLoadingProtection, isLoadingSignature, isLoadingCoupons, setIsLoadingCoupons } = useContext(UiContext);
  const uiContext = useContext(UiContext);
  const { setData: setCartTotalsData, cartTotals } = useCartTotals();
  const { getWithShippingMethodPayload } = useAddressApi();
  const methods = useSelector(selectMethods);
  const shipping = useSelector(selectShipping);
  const { getShoppercurrency } = useCurrency();

  const isAddressFilledOut = contextShippingAddress && contextShippingAddress?.userInputSingleLine;

  const handleRemove = async () => {
    setIsLoadingCoupons(true);
    const payload = getWithShippingMethodPayload(
      shipping.address,
      shipping.fullName.value,
      cartId,
      shipping.methods.selectedKey,
      shipping.additionalData?.companyName,
      additionalCharges,
    );
    const removeCouponResult = await removeCoupon({
      payload,
      cartId,
      code: isCoupon ? coupon.code : gift.code,
      deviceId: device?.id,
      email,
      contextShippingAddress,
      isAddressFilledOut,
      shippingMethodId,
      methods,
      isCoupon,
      setPaymentMethodData,
      additionalCharges,
    });
    if (removeCouponResult?.paymentMethodData) {
      setPaymentMethodData(removeCouponResult.paymentMethodData);
    }
    const { refreshedCertificates } = await refreshCertificatesAndCoupons({ giftCertificates });
    if (removeCouponResult && removeCouponResult.cartTotals) {
      const newCartTotals = { ...cartTotals.data, ...removeCouponResult.cartTotals };
      setCartTotalsData(newCartTotals);
    }
    if (isAddressFilledOut) {
      updateShippingMethods(removeCouponResult.shipMethods);
      updateShippingMethodId(removeCouponResult.currentMethod);
    }
    if (isCoupon) {
      updateCoupon({});
    }
    if (refreshedCertificates.length > 0) {
      updateGiftInfo(refreshedCertificates);
    } else if (removeCouponResult.giftCertificates) {
      updateGiftInfo(removeCouponResult?.giftCertificates);
    }
    setPaymentMethodData(removeCouponResult.paymentMethodData);
    setIsLoadingCoupons(false);
  };

  const renderTotal = () => {
    if (isLoadingCoupons) {
      return (
        <img
          className="button-position loading"
          alt="Loading..."
          src={`${currentBaseUrl()}loaders/spinner-blue.png`}
          width={15}
          height={15}
        />
      );
    }
    return (
      <input
        className="button-position"
        type="image"
        alt="Remove Coupon"
        src={`${currentBaseUrl()}assets/close.svg`}
        onClick={handleRemove}
        disabled={isLoadingProtection || isLoadingSignature}
      />
    );
  };

  const figureOutExpectedValue = () => {
    if (isCoupon) {
      return coupon.amount;
    }
    if (gift) {
      const floatGiftBalance = parseFloat(gift.balance);
      const floatGrandTotal = parseFloat(cartTotals.data?.grandTotal);
      const isGiftGreaterThanTotal = floatGiftBalance > floatGrandTotal;
      if (isGiftGreaterThanTotal) {
        return gift.used;
      }
      if (gift.applied) {
        // This condition was created for Magento
        return gift.applied;
      }
      return gift.balance;
    }
    return 0;
  };

  return (
    <div className="coupon-total">
      <div className="coupon-info">
        <span className="coupon-title">{isCoupon ? t('coupon_title') : t('gift_total_title')}</span>
        <span className="coupon-code-wrapper">
          (<span className="coupon-code">{isCoupon ? coupon.code : gift.code}</span>)
        </span>
        <div className="mgl-10">{!uiContext.done && renderTotal()}</div>
      </div>
      <div>
        <span>
          {amountFormatter({
            shopperCurrency: getShoppercurrency(),
            value: figureOutExpectedValue(),
            isNegative: true,
          })}
        </span>
      </div>
    </div>
  );
};

CouponTotalContainer.propTypes = {
  gift: PropTypes.oneOfType([PropTypes.object]),
  type: PropTypes.oneOf(['coupon', 'gift']),
};

export default CouponTotalContainer;
